// Drag-and-drop reposition for any [data-widget] HUD element.
//
// Behavior:
// - Every element with [data-widget="<id>"] gets a thin grab handle at its
//   top edge (visible on hover).
// - Mouse-down on the handle locks the element to position: fixed at its
//   current screen coords, then drag moves it.
// - Mouse-up persists the position to localStorage['hud-layout'][<id>].
// - Right-click the handle to reset that widget to its default (clears the
//   saved position and removes inline styles).
// - A drag released within SNAP_DIST of a slot in either rail takes that slot:
//   the rail is an ordered stack and DOM order is the order, so a drop sets
//   where the panel sits in it.
// - A drag released on the top or bottom edge of a panel that has been moved
//   docks to it: flush, and it follows that panel from then on.
// - On init, any previously-saved positions are re-applied.
// - Grabbing a widget raises it above the others, so the one you reached for
//   is the one you get.
// - The handle re-attaches itself if the panel redraws its own contents.

const STORAGE_KEY = 'hud-layout';

declare global {
  interface Window {
    /** The control: leave the stylesheet's centring transform in place, for `--inject drag-centred`. */
    __hudDragKeepTransform?: boolean;
    /** The control: a press counted as a drag, for `--inject fold-any-press`. */
    __hudPressIsDrag?: boolean;
    /** The control: a drop that stays pinned however near a slot, for `--inject drop-pinned`. */
    __hudSnapOff?: boolean;
    /** The control: the panel's own slot the only snap target, for `--inject reorder-off`. */
    __hudReorderOff?: boolean;
    /** The control: a moved panel offering no edge to dock to, for `--inject dock-off`. */
    __hudDockOff?: boolean;
    /** The control: a dock that never follows its anchor, for `--inject anchor-static`. */
    __hudAnchorStatic?: boolean;
    /** The control: the window as every panel's box, for `--inject drag-to-window`. */
    __hudPaneBoundsOff?: boolean;
  }
}

/** How far the pointer carries a panel before the gesture counts as a drag.
 *
 *  A press pins the panel where it is drawn and takes it out of its rail's
 *  flow, so the column under it rises into the space it left and the next
 *  header is drawn beneath the pinned box - which the fold rule in
 *  src/hudMinimize.ts would otherwise read as the player covering it. A slip
 *  of a few px is that, not a placement. */
const DRAG_SLOP = 24;

/** How near the slot it came out of a drop has to land to rejoin the flow.
 *
 *  Twice DRAG_SLOP and about one collapsed header tall, so a gesture that has
 *  only just become a drag goes back where it started; and a fifth of the
 *  220px rail, so a panel parked beside a rail is not pulled into it. */
const SNAP_DIST = 48;

/** The rails, in the order `homes` in src/hudMinimize.ts numbers them. */
export const HUD_RAIL_SELECTORS: readonly string[] = ['.hud-rail-left', '.hud-rail-right'];

/** What "flush" is worth between a docked panel and its anchor. The same gap
 *  the fit pass keeps off the window's floor, so a docked pair reads as one
 *  column rather than as two panels that happen to touch. */
const DOCK_GAP = 6;

/** Put there by the player, rather than pinned by a press that went nowhere.
 *  The fold rule in hudMinimize reads this to tell a cover from a reflow. */
function markDragged(el: HTMLElement): void {
  el.dataset['hudDragged'] = '1';
}

/**
 * Next z-index to hand out when a panel is grabbed.
 *
 * Without this, two repositioned panels overlap in DOM order and the one you
 * just reached for can sit UNDER the one you did not — which reads as the drag
 * having failed. Starts above the .repositioned rule in the stylesheet so the
 * first grab already wins.
 */
let topZ = 50;

interface SavedPosition {
  x: number;
  y: number;
}
type LayoutMap = Record<string, SavedPosition>;

/** Which panel a docked one hangs off, and which of its two edges. */
interface SavedDock {
  anchor: string;
  side: 'top' | 'bottom';
}

/**
 * Everything the player arranged: where the floating panels are, the order of
 * each rail, and which panels hang off which.
 *
 * Versioned because the shape grew: v1 was the bare `{id: {x, y}}` map and is
 * still on disk in every profile that has moved a panel, so a payload with no
 * `v` is read as that map rather than discarded, which would reset a layout
 * somebody chose.
 */
interface LayoutStore {
  v: 2;
  /** Marks a layout saved with the delta-v map in the left rail; without it the map stays in the right rail. */
  g?: 2;
  pos: LayoutMap;
  rails: Record<string, string[]>;
  dock: Record<string, SavedDock>;
}

function emptyStore(): LayoutStore {
  return { v: 2, g: 2, pos: {}, rails: {}, dock: {} };
}

function isWidget(id: string): boolean {
  return document.querySelector(`[data-widget="${CSS.escape(id)}"]`) !== null;
}

/**
 * Stored positions, with anything that is not one dropped.
 *
 * A payload from an older build, from another game on the same origin, or
 * typed in by hand must not be able to strand a panel: an id nothing answers
 * to, a coordinate that is not a finite number, or a payload that is not an
 * object at all is discarded here rather than applied and clamped later.
 */
function readPositions(parsed: unknown): LayoutMap {
  const out: LayoutMap = {};
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return out;
  for (const [id, value] of Object.entries(parsed as Record<string, unknown>)) {
    if (typeof value !== 'object' || value === null) continue;
    const { x, y } = value as { x?: unknown; y?: unknown };
    if (typeof x !== 'number' || typeof y !== 'number') continue;
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    if (!isWidget(id)) continue;
    out[id] = { x, y };
  }
  return out;
}

/** A rail's order, as ids: a rail index nothing answers to, a repeat, or an id
 *  no panel carries would each leave the rail short a panel when it is applied. */
function readRails(parsed: unknown): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return out;
  for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
    const rail = Number(key);
    if (!Number.isInteger(rail) || rail < 0 || rail >= HUD_RAIL_SELECTORS.length) continue;
    if (!Array.isArray(value)) continue;
    const ids: string[] = [];
    for (const id of value) {
      if (typeof id !== 'string' || ids.includes(id) || !isWidget(id)) continue;
      ids.push(id);
    }
    if (ids.length) out[key] = ids;
  }
  return out;
}

/** A chain that comes back to where it started has no panel at its root to
 *  place the rest against, so every entry on it is dropped. */
function dropCycles(dock: Record<string, SavedDock>): Record<string, SavedDock> {
  const out: Record<string, SavedDock> = {};
  for (const [id, d] of Object.entries(dock)) {
    const seen = new Set<string>([id]);
    let cur: string | undefined = d.anchor;
    let ok = true;
    while (cur !== undefined) {
      if (seen.has(cur)) { ok = false; break; }
      seen.add(cur);
      cur = dock[cur]?.anchor;
    }
    if (ok) out[id] = d;
  }
  return out;
}

function readDocks(parsed: unknown): Record<string, SavedDock> {
  const out: Record<string, SavedDock> = {};
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return out;
  for (const [id, value] of Object.entries(parsed as Record<string, unknown>)) {
    if (typeof value !== 'object' || value === null) continue;
    const { anchor, side } = value as { anchor?: unknown; side?: unknown };
    if (typeof anchor !== 'string' || anchor === id) continue;
    if (side !== 'top' && side !== 'bottom') continue;
    if (!isWidget(id) || !isWidget(anchor)) continue;
    out[id] = { anchor, side };
  }
  return dropCycles(out);
}

function loadStore(): LayoutStore {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch (e) {
    console.warn('hud layout load failed', e);
    return emptyStore();
  }
  if (!raw) return emptyStore();
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    console.warn('hud layout load failed', e);
    return emptyStore();
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return emptyStore();
  const shape = parsed as Record<string, unknown>;
  if (Object.keys(shape).length === 0) return emptyStore();
  if (shape['v'] !== 2) return { v: 2, pos: readPositions(parsed), rails: {}, dock: {} };
  return {
    v: 2,
    ...(shape['g'] === 2 ? { g: 2 as const } : {}),
    pos: readPositions(shape['pos']),
    rails: readRails(shape['rails']),
    dock: readDocks(shape['dock']),
  };
}

function saveStore(store: LayoutStore): void {
  // An empty layout is an empty object in either shape, and writing it that
  // way keeps "nothing is stored" one string for a reader to check.
  const empty = Object.keys(store.pos).length === 0
    && Object.keys(store.rails).length === 0
    && Object.keys(store.dock).length === 0;
  try {
    localStorage.setItem(STORAGE_KEY, empty ? '{}' : JSON.stringify(store));
  } catch (e) {
    console.warn('hud layout save failed', e);
  }
}

function loadLayout(): LayoutMap {
  return loadStore().pos;
}

function saveLayout(layout: LayoutMap): void {
  const store = loadStore();
  store.pos = layout;
  saveStore(store);
}

// ---------- The box a panel may be dragged around in ----------
//
// A sim widget belongs to the window. An Atlas panel belongs to the pane the
// Atlas is drawn into, which is the menu modal while the Solar System view is
// open - so the window is the wrong box twice over: a drag carries the panel
// out of the modal it is part of, and pinning it hands its percentage height
// and its shrink-to-fit width the window to resolve against, so it grows as it
// goes. Held on the element rather than by id: the Atlas rebuilds its panels
// on every mount, and a WeakMap lets the last mount's go.

type PaneBounds = () => DOMRect | null;
const paneBounds = new WeakMap<HTMLElement, PaneBounds>();

interface HostBox { left: number; top: number; right: number; bottom: number }

function hostBox(el: HTMLElement): HostBox {
  const r = window.__hudPaneBoundsOff ? null : paneBounds.get(el)?.() ?? null;
  if (r === null || r.width < 1 || r.height < 1) {
    return { left: 0, top: 0, right: window.innerWidth, bottom: window.innerHeight };
  }
  return { left: r.left, top: r.top, right: r.right, bottom: r.bottom };
}

/** Hold the box the pane gave the panel, measured off its own flow, so going
 *  fixed cannot resize it. Released when it goes home or when the pane changes
 *  size under it. */
function holdPaneBox(el: HTMLElement): void {
  if (window.__hudPaneBoundsOff) return;
  if (!paneBounds.has(el) || el.dataset['hudHeld'] === '1') return;
  const r = flowRect(el);
  if (r.width < 1 || r.height < 1) return;
  el.dataset['hudHeld'] = '1';
  el.style.width = `${Math.round(r.width)}px`;
  el.style.maxHeight = `${Math.round(r.height)}px`;
}

function releasePaneBox(el: HTMLElement): void {
  if (el.dataset['hudHeld'] !== '1') return;
  delete el.dataset['hudHeld'];
  el.style.removeProperty('width');
  el.style.removeProperty('max-height');
}

function applyFixedPosition(el: HTMLElement, x: number, y: number): void {
  // Clamp inside the box the panel belongs to so users can't lose a widget
  // off-screen. A panel that has no size yet - the pilot cluster ships hidden -
  // cannot be clamped against itself, which is why clampHudPositions runs again
  // once it has one.
  const host = hostBox(el);
  holdPaneBox(el);
  const hw = host.right - host.left;
  const hh = host.bottom - host.top;
  const w = el.offsetWidth;
  const h = el.offsetHeight;
  const maxX = host.left + Math.max(0, hw - Math.min(w, hw));
  const maxY = host.top + Math.max(0, hh - Math.min(h, hh));
  const cx = Math.max(host.left, Math.min(maxX, x));
  const cy = Math.max(host.top, Math.min(maxY, y));
  el.style.position = 'fixed';
  el.style.left = `${cx}px`;
  el.style.top = `${cy}px`;
  el.style.right = 'auto';
  el.style.bottom = 'auto';
  el.style.margin = '0';
  // A stylesheet rule centring a widget (the action bar's own
  // `left: 50%; transform: translateX(-50%)`) is still in effect once we've
  // pinned a `left` of our own, and doubles up with it: the drawn box sits
  // half its own width left of the `left` we just gave it. Left uncleared,
  // every apply reads its own already-shifted rect back and shifts it again.
  if (!window.__hudDragKeepTransform) el.style.transform = 'none';
  el.classList.add('repositioned');
}

function clearFixedPosition(el: HTMLElement): void {
  releasePaneBox(el);
  el.style.removeProperty('position');
  el.style.removeProperty('left');
  el.style.removeProperty('top');
  el.style.removeProperty('right');
  el.style.removeProperty('bottom');
  el.style.removeProperty('margin');
  el.style.removeProperty('z-index');
  el.style.removeProperty('transform');
  el.classList.remove('repositioned');
  delete el.dataset['hudDragged'];
  const id = el.dataset['widget'];
  if (id !== undefined) releaseDocks(id);
}

// ---------- Docks: a panel hung off one the player moved ----------
//
// A moved panel is in no rail, so nothing in the flow places what the player
// puts under it. A dock is that missing relationship, held here rather than
// derived from the boxes: two panels that happen to be flush are not docked,
// and a dock survives the anchor being collapsed into the stack and brought
// back, where the boxes do not exist to be read.

const docks = new Map<string, SavedDock>();

/** Set once by initHudDrag, so a panel makeDraggable wires up LATER - the
 *  Atlas builds its control and sampler panels long after startup - is still
 *  requeued when its own size changes, not only on a window resize. */
let dockObserver: ResizeObserver | null = null;

function widgetEl(id: string): HTMLElement | null {
  return document.querySelector<HTMLElement>(`[data-widget="${CSS.escape(id)}"]`);
}

function persistDocks(): void {
  const store = loadStore();
  store.dock = Object.fromEntries(docks);
  saveStore(store);
}

/** Let go of a panel's own dock and of everything hanging off it. Called
 *  wherever a panel goes home, which is the one place the relationship stops
 *  meaning anything. */
function releaseDocks(id: string): void {
  let changed = docks.delete(id);
  for (const [child, d] of [...docks]) {
    if (d.anchor === id) { docks.delete(child); changed = true; }
  }
  if (changed) persistDocks();
}

/** Everything that hangs off a panel, however many links down. A drag cannot
 *  offer one of these as an anchor, or the chain would have no root. */
function dockDependents(id: string): Set<string> {
  const out = new Set<string>();
  for (let pass = 0; pass < docks.size; pass++) {
    let grew = false;
    for (const [child, d] of docks) {
      if (out.has(child)) continue;
      if (d.anchor === id || out.has(d.anchor)) { out.add(child); grew = true; }
    }
    if (!grew) break;
  }
  return out;
}

/**
 * Put every docked panel back against its anchor.
 *
 * A chain settles from its root down, so the pass runs until nothing moves,
 * bounded by the number of docks. An anchor in the top-right stack is placed
 * by the stylesheet and by relayoutStack, and its own box says nothing about
 * where the player put it, so a dock waits for it to come back rather than
 * following it into the stack.
 *
 * Returns how many moved, which is what the headless reading asserts on.
 */
export function applyHudDocks(persist = true): number {
  if (window.__hudAnchorStatic || docks.size === 0) return 0;
  let moved = 0;
  for (let pass = 0; pass < docks.size; pass++) {
    let changed = 0;
    for (const [id, d] of docks) {
      const el = widgetEl(id);
      const anchor = widgetEl(d.anchor);
      if (!el || !anchor) continue;
      if (el.classList.contains('hud-stacked') || anchor.classList.contains('hud-stacked')) continue;
      if (!anchor.classList.contains('repositioned')) continue;
      const ar = anchor.getBoundingClientRect();
      const r = el.getBoundingClientRect();
      if (ar.width === 0 || ar.height === 0 || r.width === 0 || r.height === 0) continue;
      const y = d.side === 'bottom' ? ar.bottom + DOCK_GAP : ar.top - DOCK_GAP - r.height;
      if (Math.abs(r.left - ar.left) < 0.5 && Math.abs(r.top - y) < 0.5) continue;
      applyFixedPosition(el, ar.left, y);
      changed++;
    }
    moved += changed;
    if (changed === 0) break;
  }
  if (moved && persist) {
    const store = loadStore();
    for (const id of docks.keys()) {
      const el = widgetEl(id);
      if (!el || !el.classList.contains('repositioned') || el.classList.contains('hud-stacked')) continue;
      const r = el.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) continue;
      store.pos[id] = { x: r.left, y: r.top };
    }
    saveStore(store);
  }
  return moved;
}

// ---------- Rail order ----------
//
// A rail is an ordered stack and the DOM is the order, so reordering one is a
// DOM move and persisting one is a list of ids. `homeOrder` is read before any
// stored order is applied, so reset has somewhere to go back to.

const homeOrder: Record<string, string[]> = {};

function railOrder(railEl: HTMLElement): string[] {
  const out: string[] = [];
  railEl.querySelectorAll<HTMLElement>(':scope > [data-widget]').forEach((el) => {
    const id = el.dataset['widget'];
    if (id !== undefined) out.push(id);
  });
  return out;
}

function readRailOrder(): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  HUD_RAIL_SELECTORS.forEach((sel, rail) => {
    const railEl = document.querySelector<HTMLElement>(sel);
    if (railEl) out[String(rail)] = railOrder(railEl);
  });
  return out;
}

/** Ids not named by the stored order keep the place the markup gave them, so a
 *  panel a later build adds to a rail still appears for a player whose stored
 *  order predates it. */
function applyRailOrder(order: Record<string, string[]>): void {
  HUD_RAIL_SELECTORS.forEach((sel, rail) => {
    const railEl = document.querySelector<HTMLElement>(sel);
    const want = order[String(rail)];
    if (!railEl || !want) return;
    for (const id of want) {
      const el = widgetEl(id);
      if (el) railEl.appendChild(el);
    }
  });
}

/** An anchor that has no box yet places nothing, and the pilot cluster ships
 *  hidden - so a dock stored against it survives a reload only if something
 *  runs again once it has one. Out of the observer's own callback, the way
 *  queueFit is, because moving a panel inside it is a notification Chrome
 *  reports as undelivered. */
let dockQueued = false;
function queueDocks(): void {
  if (dockQueued || docks.size === 0) return;
  dockQueued = true;
  requestAnimationFrame(() => {
    dockQueued = false;
    applyHudDocks();
  });
}

/** src/hudMinimize.ts reads the rails' DOM order into `homes` once; a reorder
 *  is the one thing that changes it, so it is told rather than left to poll. */
let layoutListener: (() => void) | null = null;

export function setHudLayoutListener(fn: (() => void) | null): void {
  layoutListener = fn;
}

/** The box the panel would take if it went back into its own flow.
 *
 *  Measured rather than derived: a slot moves with every other panel in its
 *  column, so the only answer that is not a guess is to put the panel back for
 *  one measurement and take it out again. Both writes land in the same task,
 *  so nothing paints in between. */
function flowRect(el: HTMLElement): DOMRect {
  if (!el.classList.contains('repositioned')) return el.getBoundingClientRect();
  const style = el.getAttribute('style') ?? '';
  for (const prop of ['position', 'left', 'top', 'right', 'bottom', 'margin', 'transform']) {
    el.style.removeProperty(prop);
  }
  el.classList.remove('repositioned');
  const r = el.getBoundingClientRect();
  if (style) el.setAttribute('style', style);
  el.classList.add('repositioned');
  return r;
}

/** The slot a release would rejoin, drawn while the panel is over it. One
 *  element, made by the first drag that comes near a slot and dropped again
 *  when that gesture ends. */
let snapGhost: HTMLElement | null = null;

function setSnapGhost(box: SnapBox | null): void {
  if (box === null) {
    snapGhost?.remove();
    snapGhost = null;
    return;
  }
  if (!snapGhost) {
    snapGhost = document.createElement('div');
    snapGhost.className = 'hud-snap-ghost';
    document.body.appendChild(snapGhost);
  }
  snapGhost.style.left = `${box.left}px`;
  snapGhost.style.top = `${box.top}px`;
  snapGhost.style.width = `${box.width}px`;
  snapGhost.style.height = `${box.height}px`;
}

// ---------- What a release can land on ----------
//
// Three shapes of target, one distance: the top-left of the box the panel
// would take, against the top-left of the box it is drawn in. A rail offers
// the top edge of each panel flowing down it and the space past the last one;
// a panel the player has moved offers its own top and bottom edges. Only a
// panel that has a slot in a rail is offered any of them - the action bar, the
// audio controls and the atlas panels are placed by the stylesheet and have no
// order to join.

interface SnapBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

type SnapTarget =
  | { kind: 'rail'; rail: HTMLElement; before: HTMLElement | null; box: SnapBox }
  | { kind: 'dock'; anchor: HTMLElement; side: 'top' | 'bottom'; box: SnapBox };

function inFlow(el: HTMLElement): boolean {
  if (el.classList.contains('repositioned') || el.classList.contains('hud-stacked')) return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

function railFlow(railEl: HTMLElement, dragged: HTMLElement): HTMLElement[] {
  const out: HTMLElement[] = [];
  railEl.querySelectorAll<HTMLElement>(':scope > [data-widget]').forEach((el) => {
    if (el !== dragged && inFlow(el)) out.push(el);
  });
  return out;
}

/** Where in the DOM an insertion before `sib` belongs: above the run of panels
 *  the flow does not show that sits immediately over it, so a drop between two
 *  visible panels does not jump a collapsed neighbour. */
function insertionRef(sib: HTMLElement, dragged: HTMLElement): HTMLElement {
  let ref = sib;
  for (;;) {
    const prev = ref.previousElementSibling;
    if (!(prev instanceof HTMLElement)) return ref;
    if (prev === dragged || prev.dataset['widget'] === undefined || inFlow(prev)) return ref;
    ref = prev;
  }
}

function railTargets(el: HTMLElement, slot: SnapBox): SnapTarget[] {
  const out: SnapTarget[] = [];
  for (const sel of HUD_RAIL_SELECTORS) {
    const railEl = document.querySelector<HTMLElement>(sel);
    if (!railEl) continue;
    const rr = railEl.getBoundingClientRect();
    const gap = parseFloat(getComputedStyle(railEl).rowGap);
    const left = rr.width > 0 ? rr.left : slot.left;
    const width = rr.width > 0 ? rr.width : slot.width;
    const flow = railFlow(railEl, el);
    if (flow.length === 0) {
      out.push({ kind: 'rail', rail: railEl, before: null, box: { left, top: rr.top, width, height: slot.height } });
      continue;
    }
    for (const sib of flow) {
      const b = sib.getBoundingClientRect();
      out.push({
        kind: 'rail', rail: railEl, before: insertionRef(sib, el),
        box: { left, top: b.top, width, height: slot.height },
      });
    }
    const last = flow[flow.length - 1];
    if (last === undefined) continue;
    const lb = last.getBoundingClientRect();
    out.push({
      kind: 'rail', rail: railEl, before: null,
      box: { left, top: lb.bottom + (Number.isFinite(gap) ? gap : 0), width, height: slot.height },
    });
  }
  return out;
}

function dockTargets(el: HTMLElement, id: string, size: SnapBox): SnapTarget[] {
  const out: SnapTarget[] = [];
  const circular = dockDependents(id);
  document.querySelectorAll<HTMLElement>('[data-widget].repositioned').forEach((anchor) => {
    if (anchor === el || anchor.classList.contains('hud-stacked')) return;
    const aid = anchor.dataset['widget'];
    if (aid === undefined || circular.has(aid)) return;
    const r = anchor.getBoundingClientRect();
    if (r.width === 0 || r.height === 0) return;
    out.push({
      kind: 'dock', anchor, side: 'bottom',
      box: { left: r.left, top: r.bottom + DOCK_GAP, width: size.width, height: size.height },
    });
    out.push({
      kind: 'dock', anchor, side: 'top',
      box: { left: r.left, top: r.top - DOCK_GAP - size.height, width: size.width, height: size.height },
    });
  });
  return out;
}

/** The nearest target inside the snap distance, or null. A rail target wins a
 *  tie: the rails are where the layout starts, so a release that could mean
 *  either gives the panel back to the flow. */
function snapTarget(el: HTMLElement, id: string, slot: SnapBox | null, size: SnapBox): SnapTarget | null {
  if (window.__hudSnapOff || slot === null) return null;
  const targets: SnapTarget[] = [];
  if (window.__hudReorderOff) {
    // The one target there used to be: the panel's own slot, put back exactly
    // where it was rather than at an index the flow was asked for.
    const railEl = el.parentElement;
    const next = el.nextElementSibling;
    if (railEl) {
      targets.push({ kind: 'rail', rail: railEl, before: next instanceof HTMLElement ? next : null, box: slot });
    }
  } else {
    targets.push(...railTargets(el, slot));
  }
  if (!window.__hudDockOff) targets.push(...dockTargets(el, id, size));
  const r = el.getBoundingClientRect();
  let best: SnapTarget | null = null;
  let bestDist = Infinity;
  for (const t of targets) {
    const d = Math.hypot(r.left - t.box.left, r.top - t.box.top);
    if (d <= SNAP_DIST && d < bestDist) { bestDist = d; best = t; }
  }
  return best;
}

/**
 * Make one element draggable by a handle at its top edge.
 *
 * Split out of initHudDrag so panels that mount LATER can use it — the Atlas
 * builds its control and sampler panels when its tab opens, long after
 * startup, so a one-shot querySelectorAll at boot never sees them.
 *
 * `bounds` is the box the panel may not be carried out of and keeps its size
 * against, for one drawn inside a pane rather than in the window.
 */
export function makeDraggable(el: HTMLElement, id: string, bounds?: PaneBounds): void {
  if (bounds) paneBounds.set(el, bounds);
  if (el.dataset['dragWired'] === '1') return;
  el.dataset['dragWired'] = '1';
  el.dataset['widget'] = id;
  dockObserver?.observe(el);

  const layout = loadLayout();
  const handle = document.createElement('div');
  handle.className = 'widget-handle';
  handle.title = 'Drag to reposition · Right-click to reset';
  el.appendChild(handle);

  // Survive the panel redrawing itself.
  //
  // Several panels rebuild their contents with innerHTML — the Atlas inspector
  // and sampler do it on every render — and that throws away any child the
  // panel did not write, including this handle. The panel kept its data-widget
  // attribute and so looked wired, but the thing you actually grab was gone,
  // which is why those two could never be dragged however they were styled.
  //
  // Putting it back is cheap and the observer cannot loop: re-appending fires
  // it again, and the second pass finds the handle present and does nothing.
  new MutationObserver(() => {
    if (!handle.isConnected || handle.parentElement !== el) el.appendChild(handle);
  }).observe(el, { childList: true });

  const saved = layout[id];
  if (saved) {
    applyFixedPosition(el, saved.x, saved.y);
    markDragged(el);
  }

  handle.addEventListener('mousedown', (e) => {
    if (e.button !== 0) return; // left only
    e.preventDefault();
    e.stopPropagation();
    const rect = el.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    const startX = e.clientX;
    const startY = e.clientY;
    const hadSaved = loadLayout()[id] !== undefined;
    let moved = false;

    // Lock to fixed at current screen coords before dragging — keeps the
    // first frame visually stable even if it was in flow / right-anchored.
    applyFixedPosition(el, rect.left, rect.top);
    el.style.zIndex = String(++topZ);
    handle.style.cursor = 'grabbing';
    // A collapsed header is pinned into the stack by the stylesheet, so it has
    // no slot of its own to be given back. Read once: the flow the panel would
    // rejoin does not change while it is pinned above it.
    const inRail = el.closest(HUD_RAIL_SELECTORS.join(', ')) !== null;
    const slot = el.classList.contains('hud-stacked') || !inRail ? null : flowRect(el);
    // The panel's own dock is let go for the length of the gesture, so the
    // follow pass does not pull it back to the anchor it is being carried away
    // from. A press that went nowhere puts it back on release.
    const hadDock = docks.get(id);
    if (hadDock) { docks.delete(id); persistDocks(); }
    const size = el.getBoundingClientRect();
    let hit: SnapTarget | null = null;

    const onMove = (ev: MouseEvent): void => {
      if (ev.buttons === 0) { onUp(); return; }
      if (!moved && Math.abs(ev.clientX - startX) + Math.abs(ev.clientY - startY) > DRAG_SLOP) {
        moved = true;
      }
      applyFixedPosition(el, ev.clientX - offsetX, ev.clientY - offsetY);
      if (moved) markDragged(el);
      // Whatever hangs off this panel comes with it, written down once on
      // release rather than on every frame of the drag.
      applyHudDocks(false);
      hit = moved ? snapTarget(el, id, slot, size) : null;
      setSnapGhost(hit === null ? null : hit.box);
    };
    const onUp = (): void => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      handle.style.cursor = '';
      setSnapGhost(null);
      // A press the pointer never carried anywhere leaves nothing behind: the
      // panel goes back into its rail's flow rather than staying pinned over
      // the neighbour that rose underneath it.
      if (!moved && !hadSaved && !window.__hudPressIsDrag) {
        clearFixedPosition(el);
        return;
      }
      // Dropped on a slot: the panel takes that slot rather than keeping a
      // fixed position aligned to one, and the rail's order is the DOM order it
      // lands in. Only a panel back in the flow is one the rail's fold rule
      // (src/hudMinimize.ts) can reach, and getting that back is half of what
      // snapping is for.
      if (hit !== null && hit.kind === 'rail') {
        clearFixedPosition(el);
        hit.rail.insertBefore(el, hit.before);
        const store = loadStore();
        delete store.pos[id];
        store.rails = readRailOrder();
        store.dock = Object.fromEntries(docks);
        saveStore(store);
        layoutListener?.();
        return;
      }
      // Dropped on a moved panel's edge: flush against it, and hung off it from
      // now on.
      if (hit !== null) {
        applyFixedPosition(el, hit.box.left, hit.box.top);
        markDragged(el);
        const anchorId = hit.anchor.dataset['widget'];
        if (anchorId !== undefined) docks.set(id, { anchor: anchorId, side: hit.side });
      } else if (!moved && hadDock) {
        docks.set(id, hadDock);
      }
      const r = el.getBoundingClientRect();
      const store = loadStore();
      store.pos[id] = { x: r.left, y: r.top };
      store.dock = Object.fromEntries(docks);
      saveStore(store);
      applyHudDocks();
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  });

  handle.addEventListener('contextmenu', (e) => {
    e.preventDefault();
    const cur = loadLayout();
    delete cur[id];
    saveLayout(cur);
    clearFixedPosition(el);
  });
}

/** `oldLayout` puts the delta-v map back at the foot of the right rail, as it was drawn before it moved under the PILOT (the `?hudoldlayout=1` control). */
export function initHudDrag(oldLayout = false): void {
  // Read before the stored order is applied: this is where reset goes back to.
  const store = loadStore();
  const oldHome = (): void => {
    const rail = document.querySelector<HTMLElement>(HUD_RAIL_SELECTORS[1] ?? '');
    const map = widgetEl('dv-map');
    if (rail !== null && map !== null) rail.appendChild(map);
  };
  const arranged = Object.keys(store.pos).length > 0 || Object.keys(store.rails).length > 0 || Object.keys(store.dock).length > 0;
  if (oldLayout) oldHome();
  Object.assign(homeOrder, readRailOrder());
  if (arranged && store.g !== 2 && !oldLayout) oldHome();
  applyRailOrder(store.rails);
  docks.clear();
  for (const [id, d] of Object.entries(store.dock)) docks.set(id, d);
  document.querySelectorAll<HTMLElement>('[data-widget]').forEach((el) => {
    const id = el.dataset['widget'];
    if (id) makeDraggable(el, id);
  });
  applyHudDocks();
  dockObserver = new ResizeObserver(() => queueDocks());
  document.querySelectorAll<HTMLElement>('[data-widget]').forEach((el) => dockObserver?.observe(el));

  // Keep widgets inside the viewport on resize
  window.addEventListener('resize', () => {
    const current = loadLayout();
    document.querySelectorAll<HTMLElement>('[data-widget].repositioned').forEach((el) => {
      const id = el.dataset['widget'];
      if (!id) return;
      const saved = current[id];
      if (!saved) return;
      // The pane is a different size now, so the box held off it is stale.
      releasePaneBox(el);
      applyFixedPosition(el, saved.x, saved.y);
    });
    applyHudDocks();
  });
}

/** True when the player has arranged anything: a panel moved, a rail reordered,
 *  or a panel hung off another. */
export function hasSavedHudLayout(): boolean {
  const store = loadStore();
  return Object.keys(store.pos).length > 0
    || Object.keys(store.rails).length > 0
    || Object.keys(store.dock).length > 0;
}

/**
 * Shove floating panels clear of a strip of screen the UI has just taken back.
 *
 * A dragged panel is position:fixed and lifted above everything, so it does not
 * belong to any pane's layout and nothing reflows it. That is exactly what you
 * want while dragging, and exactly wrong when the menu rail slides back in
 * underneath one: the panel sits on top of the navigation it is covering.
 *
 * Only panels that actually intrude are touched, and only far enough to clear —
 * a panel the user put somewhere deliberate on the right stays there. The new
 * position is persisted, because the old one is no longer somewhere the panel
 * can be.
 *
 * Returns how many moved, which is what the headless check asserts on.
 */
export function pushHudPositionsClearOf(minLeft: number, gap = 10): number {
  const layout = loadLayout();
  let moved = 0;
  document.querySelectorAll<HTMLElement>('[data-widget].repositioned').forEach((el) => {
    const id = el.dataset['widget'];
    if (!id) return;
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.left >= minLeft) return;
    applyFixedPosition(el, minLeft + gap, rect.top);
    const after = el.getBoundingClientRect();
    layout[id] = { x: after.left, y: after.top };
    moved++;
  });
  if (moved) saveLayout(layout);
  return moved;
}

/**
 * Put every moved panel back inside the window it is drawn in.
 *
 * A position is stored before a panel has a size (hidden at boot), and a
 * window can be made smaller after one was stored, so the clamp at apply time
 * is not the last word. Returns how many had to move, which is what the
 * headless reading asserts on.
 */
export function clampHudPositions(): number {
  const layout = loadLayout();
  let moved = 0;
  document.querySelectorAll<HTMLElement>('[data-widget].repositioned').forEach((el) => {
    const id = el.dataset['widget'];
    if (!id) return;
    const before = el.getBoundingClientRect();
    if (before.width === 0 && before.height === 0) return;
    applyFixedPosition(el, before.left, before.top);
    const after = el.getBoundingClientRect();
    if (Math.abs(after.left - before.left) < 0.5 && Math.abs(after.top - before.top) < 0.5) return;
    layout[id] = { x: after.left, y: after.top };
    moved++;
  });
  if (moved) saveLayout(layout);
  return moved;
}

interface Box {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

function boxOf(el: HTMLElement): Box {
  const r = el.getBoundingClientRect();
  return { left: r.left, top: r.top, right: r.right, bottom: r.bottom, width: r.width, height: r.height };
}

function hits(a: Box, b: Box): boolean {
  return a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;
}

/**
 * Move floating panels out of a panel that has just been put back.
 *
 * A panel the player dragged out is position:fixed and in no rail, so the fold
 * pass in hudMinimize.ts can neither fold it nor fold anything to make room for
 * it: a floating panel restored to full height grows into its neighbours, and a
 * panel restored underneath one comes back covered. The flow owns its own
 * boxes, so the floating panel is the one that gives way, to the nearest edge
 * that leaves it on screen.
 *
 * `ids` is the set the reading is over - the panels the fold pass owns - so a
 * band the player parked a panel on deliberately is not an obstacle.
 *
 * Returns how many moved, which is what the headless reading asserts on.
 */
export function pushHudPositionsClearOfPanel(ids: readonly string[], restoredId: string, gap = 8): number {
  const panels = new Map<string, HTMLElement>();
  for (const id of ids) {
    const el = document.querySelector<HTMLElement>(`[data-widget="${CSS.escape(id)}"]`);
    if (!el) continue;
    const b = boxOf(el);
    if (b.width === 0 || b.height === 0) continue;
    panels.set(id, el);
  }
  const target = panels.get(restoredId);
  if (!target) return 0;
  // A collapsed header in the stack is placed by the stylesheet and by
  // relayoutStack, so moving it here would only fight them.
  // A docked panel is placed by its anchor, so moving it here would only be
  // undone by the next follow pass; the anchor is what the player moves.
  const floats = (el: HTMLElement): boolean =>
    el.classList.contains('repositioned') && !el.classList.contains('hud-stacked')
    && !docks.has(el.dataset['widget'] ?? '');
  const movers: string[] = [];
  if (floats(target)) {
    movers.push(restoredId);
  } else {
    const tb = boxOf(target);
    for (const [id, el] of panels) {
      if (id !== restoredId && floats(el) && hits(boxOf(el), tb)) movers.push(id);
    }
  }
  if (movers.length === 0) return 0;
  const layout = loadLayout();
  let moved = 0;
  for (const id of movers) {
    const el = panels.get(id);
    if (!el) continue;
    const start = boxOf(el);
    for (let pass = 0; pass < panels.size; pass++) {
      const b = boxOf(el);
      let worst: Box | null = null;
      let worstArea = 0;
      for (const [otherId, otherEl] of panels) {
        if (otherId === id) continue;
        const o = boxOf(otherEl);
        if (!hits(b, o)) continue;
        const area = (Math.min(b.right, o.right) - Math.max(b.left, o.left))
          * (Math.min(b.bottom, o.bottom) - Math.max(b.top, o.top));
        if (area > worstArea) { worstArea = area; worst = o; }
      }
      if (worst === null) break;
      let pick: { x: number; y: number } | null = null;
      let pickCost = Infinity;
      for (const c of [
        { x: worst.left - b.width - gap, y: b.top },
        { x: worst.right + gap, y: b.top },
        { x: b.left, y: worst.top - b.height - gap },
        { x: b.left, y: worst.bottom + gap },
      ]) {
        if (c.x < 0 || c.y < 0) continue;
        if (c.x + b.width > window.innerWidth || c.y + b.height > window.innerHeight) continue;
        const cost = Math.abs(c.x - b.left) + Math.abs(c.y - b.top);
        if (cost < pickCost) { pickCost = cost; pick = c; }
      }
      // Nowhere on screen clears it. Leaving it where the player put it beats
      // clamping it back onto the panel it is already covering.
      if (pick === null) break;
      applyFixedPosition(el, pick.x, pick.y);
    }
    const end = boxOf(el);
    if (Math.abs(end.left - start.left) < 0.5 && Math.abs(end.top - start.top) < 0.5) continue;
    layout[id] = { x: end.left, y: end.top };
    moved++;
  }
  if (moved) saveLayout(layout);
  applyHudDocks();
  return moved;
}

/** Everything home: no stored position, no dock, and each rail back in the
 *  order the markup gave it. */
export function resetAllHudPositions(): void {
  docks.clear();
  saveStore(emptyStore());
  document.querySelectorAll<HTMLElement>('[data-widget].repositioned').forEach((el) => {
    clearFixedPosition(el);
  });
  applyRailOrder(homeOrder);
  layoutListener?.();
}
